import { message } from "antd";
import _ from "lodash";
import moment from "moment";
import { LightenDarkenColor } from "lighten-darken-color";
import { TooltipParagraph } from "@iiht/components";
import { directLoginStorage, localStorageHelper } from "./storageHelper";
import { roleName, sixthTimeFormat, dateTimeFormat3 } from "../constants/componentConstants";

export const accessChecker = (action, permissions) => {
  if (!action || !permissions) {
    return false;
  }
  return permissions.includes(action);
};

export const getSortOrder = (sortField, sortOrder) => {
  if (!sortField || !sortOrder) {
    return false;
  }

  if (sortField === sortOrder) {
    return "-".concat(sortField);
  } else {
    return sortField;
  }
};

export const uuidv4 = () => {
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  );
};

export const getPaginationParams = (dataPerPage, currentPageNumber, sortOrder) => {
  if (!dataPerPage || !currentPageNumber || !sortOrder) {
    return false;
  }
  return "?limit=" + dataPerPage + "&offset=" + (currentPageNumber - 1) * dataPerPage + "&ordering=" + sortOrder;
};

export const getPaginationDateParams = (from, dataPerPage, currentPageNumber, sortOrder, to, status) => {
  if (!from || !dataPerPage || !currentPageNumber || !sortOrder || !to) {
    return false;
  }
  return (
    "?from=" +
    from +
    "&limit=" +
    dataPerPage +
    "&offset=" +
    (currentPageNumber - 1) * dataPerPage +
    "&ordering=" +
    sortOrder +
    "&to=" +
    to +
    "&level=" +
    status
  );
};

export const checkIfObjectNotEmpty = (obj) => {
  if (obj && Object.keys(obj).length > 0) {
    return true;
  }
  return false;
};

export const appendParam = (url, urlParamName, urlParamValue) => {
  if (urlParamName || urlParamValue) {
    let urlParam = "";
    if (url.includes("?")) {
      urlParam = "&".concat(urlParamName).concat("=").concat(urlParamValue);
    } else {
      urlParam = "?".concat(urlParamName).concat("=").concat(urlParamValue);
    }
    return url.concat(urlParam);
  }
  return false;
};

export const debounce = (callback, wait = 300) => {
  let timeoutId = null;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      callback.apply(null, args);
    }, wait);
  };
};

export const checkIfSuccessAPI = (status, apiStatus) => {
  return apiStatus?.status === status;
};

export const differenceBetweenDates = (startDate, endDate, dateFormat) => {
  return Math.abs(moment(startDate, dateFormat)?.startOf("day")?.diff(moment(endDate, dateFormat)?.startOf("day"), "days")) + 1;
};

export const mapperForRoles = (roles) => {
  return roles.map((data) => ({
    label: data?.role_name,
    key: data?.role_id,
    value: data?.role_id,
  }));
};

export const createListMapper = (list) => {
  return list.map((data, index) => ({
    label: data,
    key: index,
    value: data,
  }));
};

export const userListMapper = (list) => {
  return list.map((data) => ({
    ...data,
    label: `${data?.name}`,
    key: data?.user_id,
    value: data?.user_id,
  }));
};

export const getLabelsFromList = (optionList, selectedList) => {
  const filtered = optionList.filter((el) => {
    return selectedList.indexOf(el.id) !== -1;
  });
  const labels = _.map(filtered, "label");
  return labels.join(", ");
};

export const dynamicListMapper = (list, label, value, key) => {
  return list.map((data) => ({
    ...data,
    label: data[label],
    key: data[key],
    value: data[value],
  }));
};

export const isPageVisited = (pages, currentPage) => {
  return pages.includes(currentPage);
};

export const getAddress = (address, str) => {
  return address ? `${address}${str}` : "";
};

export const getLabels = (list, key) => {
  return _.map(list, key);
};

export const userNameMapper = (list, key1, key2) => {
  return list.map((data) => ({
    ...data,
    label: key2 ? `${data[key1]} ${data[key2]}` : `${data[key1]}`,
    key: data?.user_id,
    value: data?.user_id,
  }));
};

const splitsTheDate = (date) => {
  if (date) {
    const datesArray = date?.split("+");
    return datesArray[0];
  }
};

export const diffBeteenDateAndTime = (expiresDate, currentDate) => {
  let expireDateAndTime = splitsTheDate(expiresDate);
  let currentDateAndTime = splitsTheDate(currentDate);
  const expireTimeInMilisecond = moment(expireDateAndTime)?.diff(moment(currentDateAndTime), "milliseconds");
  return expireTimeInMilisecond;
};

const updateUrl = (base_url, name, value, appendContinue) => {
  return base_url.endsWith("&") || base_url.endsWith("?")
    ? `${base_url}${name}=${value}${appendContinue ? "&" : ""}`
    : `${base_url}&${name}=${value}${appendContinue ? "&" : ""}`;
};

export const addFilterToUrl = (base_url, filterChange, resourceProviderId) => {
  let data = Object?.values(filterChange)?.filter((el) => el !== null);

  if (data?.length === 1) {
    for (const el in filterChange) {
      if (filterChange[el]) {
        base_url = updateUrl(base_url, el, filterChange[el]);
      }
    }
  }

  if (data?.length > 1) {
    for (const el in filterChange) {
      if (filterChange[el] !== "") {
        base_url = updateUrl(base_url, el, filterChange[el], true);
      }
    }
  }

  if (base_url?.endsWith("&")) {
    base_url = base_url.slice(0, base_url.length - 1);
  }

  if (resourceProviderId) {
    base_url = appendParam(base_url, "resource_provider", resourceProviderId);
  }
  return { base_url };
};

export const dropdownOptionsMapper = (list) => {
  return Object.entries(list)?.reduce(
    (result, currItem) => ({
      ...result,
      [currItem?.[0]]: [
        {
          label: currItem?.[1]?.["display_name"],
          key: currItem?.[1]?.["file"],
          value: currItem?.[1]?.["file"],
        },
      ],
    }),
    {}
  );
};

export const fixAutocomplete = () => {
  document.querySelectorAll(".ant-select-selection-search-input").forEach((e) => {
    e.setAttribute("autocomplete", "stopAutocomplete");
  });
};

export const renderTableCell = (result, record, classNames = "", isLink, clickEv, TooltipParagraphClsNames = "") =>
  isLink ? (
    <u className={`${classNames} headingColor`} onClick={() => (clickEv ? clickEv(record) : {})}>
      <TooltipParagraph>{result}</TooltipParagraph>
    </u>
  ) : (
    <span className={`${classNames} headingColor`}>
      <TooltipParagraph className={TooltipParagraphClsNames} isNotification>
        {result}
      </TooltipParagraph>
    </span>
  );

export const getProgressStats = (stats) => {
  switch (parseInt(stats)) {
    case 1:
    case 2:
    case 3:
    case 4:
    case 5:
    case 6:
    case 7:
    case 8:
    case 9:
    case 10:
    case 11:
    case 12:
      return 10;
    case 13:
    case 14:
    case 15:
      return 20;
    case 16:
    case 17:
    case 18:
      return 40;
    case 19:
    case 20:
    case 21:
      return 60;
    case 22:
    case 23:
    case 24:
      return 80;
    case 26:
      return 100;
    case 25:
      return 95;
    default:
      return 5;
  }
};

export const getReportsStats = (stats) => {
  switch (parseInt(stats)) {
    case 1:
      return 16;
    case 2:
      return 32;
    case 3:
      return 48;
    case 4:
      return 64;
    case 5:
      return 80;
    case 6:
      return 100;
    case 7:
      return 70;
    default:
      return 5;
  }
};

export const getIdInObject = (list, key) => {
  return list.map((id) => {
    return {
      [key]: id,
    };
  });
};

export const getIdFromObject = (list, key) => {
  return list.map((data) => {
    return data[key];
  });
};

export const shortenText = (word, maxLimit) => {
  return word?.length > maxLimit ? word?.slice(0, maxLimit) + "..." : word;
};
export const capLimit = (digit, limit = 3) => {
  if (digit || digit === 0) {
    const len = digit?.toString?.()?.length;
    if (len > limit) {
      return "9".repeat(limit) + "+";
    } else {
      return digit;
    }
  } else {
    return "";
  }
};

export const sortOrderHandler = (setSortOrder, sortField, setCurrentPageNumber, defaultCurrentPage) => {
  return {
    onClick: () => {
      setSortOrder((sort) => getSortOrder(sortField, sort));
      setCurrentPageNumber(defaultCurrentPage);
    },
  };
};

export const getDateDifference = (expiryDate, decommissionDate) => {
  //check for date difference if decommission date is present else return default obj
  if (expiryDate && decommissionDate) {
    //Convert date strings to moment objects and normalize to start of day
    const expiryDateObj = moment(expiryDate).startOf("day");
    const decommissionDateObj = moment(decommissionDate).startOf("day");
    const currentDateObj = moment().startOf("day");
    // Check if current date is on or after expiry date
    if (currentDateObj.isSameOrAfter(expiryDateObj)) {
      const timeDifference = decommissionDateObj.diff(currentDateObj, "hours", true);
      const hourInSeconds = 1000 * 60 * 60; // kept for compatibility
      const hours = timeDifference.toFixed(2);
      const flag =
        currentDateObj.isSameOrAfter(expiryDateObj) &&
        currentDateObj.isSameOrBefore(decommissionDateObj);

      return { hours, flag };
    }
  }

  return {
    hours: 0,
    flag: false,
  };
};

export const getYYYY_MM_DD = (dt) => {
  const offset = dt?.getTimezoneOffset?.();
  dt = new Date(dt?.getTime?.() - offset * 60 * 1000);
  return dt?.toISOString?.().split?.("T")?.[0];
};

export const isImgSizeLessThanLimit = (imgSize, limit) => {
  return imgSize / 1024 / 1024 >= limit;
};

export const isValidDocType = (extensionSet, docType) => {
  return !extensionSet?.includes(docType);
};

export const getEnv = () => {
  if (window.location.href.includes("dev")) {
    return "https://dev.makemylabs.in";
  } else if (window.location.href.includes("qa")) {
    return "https://qa.makemylabs.in";
  } else if (window.location.href.includes("perf")) {
    return "https://perf.makemylabs.in";
  } else if (window.location.href.includes("uat")) {
    return "https://uat.makemylabs.in";
  } else if (window.location.href.includes("laas")) {
    return "https://laas.makemylabs.in";
  } else if (window.location.href.includes("stag")) {
    return "https://stage.makemylabs.in";
  } else if (window.location.href.includes("hotfix")) {
    return "https://hotfix.makemylabs.in";
  } else if (window.location.href.includes("localhost:3000")) {
    return "http://localhost:3000";
  } else {
    return "https://laas.makemylabs.in";
  }
};

export const getUserRole = () => {
  let [role] = (localStorageHelper.get("role_name") && JSON.parse(localStorageHelper.get("role_name"))) || "";
  return role;
};

export const random = (min, max) => Math.floor(Math.random() * (max - min)) + min;

export const checkIfEleExistsByClsNm = (classname) => {
  return document.getElementsByClassName(classname)?.length > 0;
};

export const filterOptions = (input, option) => {
  return option?.label?.toLowerCase()?.indexOf(input?.toLowerCase()) >= 0;
};

export const clearStorage = () => {
  localStorageHelper.clear();
  sessionStorage.clear();
  directLoginStorage.clear();
};

export const storageCleanUpFunction = () => {
  let url = getTenantUrl();
  localStorageHelper.clear();
  sessionStorage.clear();
  directLoginStorage.clear();
  setTimeout(() => {
    window.location.replace(url);
  }, 3000);
};

export const storageCleanUpFunctionForWorkspace = () => {
  let url = getWorkspaceUrl();
  localStorageHelper.clear();
  sessionStorage.clear();
  directLoginStorage.clear();
  setTimeout(() => {
    window.location.replace(url);
  }, 3000);
};

export const getLogoutData = () => {
  if (JSON.parse(localStorageHelper.get("is_portal_login"))) {
    return {
      tenantid: localStorageHelper.get("tenantId"),
      sessionid: localStorageHelper.get("sessionId"),
      userID: localStorageHelper.get("userId"),
      tenantUrl: localStorageHelper.get("tenantUrl"),
    };
  } else {
    let url = localStorageHelper.get("wsmTenantUrl");

    return {
      tenantid: localStorageHelper.get("wsmTenantId"),
      sessionid: localStorageHelper.get("sessionid"),
      userID: localStorageHelper.get("userId"),
      workspace_url: `/wsm${url?.split("wsm")?.[1]}`,
    };
  }
};

export const setCustomThemeInLocalStorage = (data) => {
  if (data && data?.custom_theme) {
    localStorageHelper.set("custom_theme", JSON.stringify(data?.custom_theme));
  }
};

export const setTokenDataInLocalStorage = (data, authType) => {
  localStorageHelper.set("authToken", data?.id_token ? data.id_token : data?.accessToken);
  localStorageHelper.set("accessToken", data?.accessToken);
  localStorageHelper.set("username", data?.logged_inusername);
  localStorageHelper.set("first_name", data?.first_name);
  localStorageHelper.set("tenant_services", data?.tenant_services);
  localStorageHelper.set("is_portal_login", data?.is_portal_login);
  localStorageHelper.set("tenant_name", data?.tenant_name);
  localStorageHelper.set("last_name", data?.last_name);
  localStorageHelper.set("localTenantId", data?.localTenantId);
  localStorageHelper.set("logged_inusername", data?.logged_inusername);
  localStorageHelper.set("role", data?.role);
  localStorageHelper.set("role_name", data?.role_name);
  localStorageHelper.set("tenantId", data?.tenantId);
  localStorageHelper.set("auth_type", data?.auth_type);
  localStorageHelper.set("sessionid", data?.sessionid);
  localStorageHelper.set("userId", data?.userId);
  localStorageHelper.set("is_mark_for_b2c", data?.is_mark_for_b2c);
  localStorageHelper.set("is_parent_tenant", data?.is_parent_tenant);
  localStorageHelper.set("child_tenant", JSON.stringify(data?.child_tenant));
  localStorageHelper.set("is_mentor", data?.is_mentor);
  setCustomThemeInLocalStorage(data);
};

export const setWsmTokenDataInLocalStorage = (data, authType) => {
  localStorageHelper.set("wsmAuthToken", data?.id_token);
  localStorageHelper.set("wsmAccessToken", data?.accessToken);
  localStorageHelper.set("username", data?.logged_inusername);
  localStorageHelper.set("first_name", data?.first_name);
  localStorageHelper.set("tenant_services", data?.tenant_services);
  localStorageHelper.set("is_portal_login", data?.is_portal_login);
  localStorageHelper.set("tenant_name", data?.tenant_name);
  localStorageHelper.set("last_name", data?.last_name);
  localStorageHelper.set("wsmLocalTenantId", data?.localTenantId);
  localStorageHelper.set("logged_inusername", data?.logged_inusername);
  localStorageHelper.set("role", data?.role);
  localStorageHelper.set("role_name", data?.role_name);
  localStorageHelper.set("wsmTenantId", data?.tenantId);
  localStorageHelper.set("wsm_auth_type", data?.auth_type);
  localStorageHelper.set("sessionid", data?.sessionid);
  localStorageHelper.set("userId", data?.userId);
  localStorageHelper.set("is_parent_tenant", data?.is_parent_tenant);
  localStorageHelper.set("child_tenant", JSON.stringify(data?.child_tenant));
  setCustomThemeInLocalStorage(data);
};

export const setIdpTokenDataInLocalStorage = (data) => {
  // localStorageHelper.set("refreshToken", data?.refresh_token); //no
  // localStorageHelper.set("authToken", data?.id_token); //no
  // localStorageHelper.set("tenantId", data?.tenantId); // no
  localStorageHelper.set("userId", data?.userId);
  localStorageHelper.set("role", data?.role);
  localStorageHelper.set("role_name", data?.role_name);
  localStorageHelper.set("username", data?.logged_inusername);
  localStorageHelper.set("first_name", data?.first_name); // no
  localStorageHelper.set("sessionId", data?.sessionid); // remove this from login component once AAD flow removed
  localStorageHelper.set("is_parent_tenant", data?.is_parent_tenant);
  localStorageHelper.set("child_tenant", JSON.stringify(data?.child_tenant));
  localStorageHelper.set("is_mentor", data?.is_mentor);
};

export const extColumns = (t) => [
  {
    title: t("common.name"),
    dataIndex: "name",
    render: (name) => <>{name} </>,
    width: "32%",
    className: "qa-extend-name",
  },
  {
    title: t("myLabs.validityDays"),
    dataIndex: "validity_days",
    render: (validity_days) => <>{validity_days} </>,
    width: "32%",
    className: "qa-extend-validity-days",
  },
  {
    title: t("myLabs.durationMinutes"),
    dataIndex: "duration_minutes",
    render: (duration_minutes) => <>{duration_minutes}</>,
    width: "32%",
    className: "qa-extened-duration-min",
  },
];

export const deleteDataTableReloadHandler = (currentPageNumber, defaultCurrentPage, loadPageData, setCurrentPageNumber, dataList) => {
  if (currentPageNumber && defaultCurrentPage && loadPageData && setCurrentPageNumber && dataList) {
    if (currentPageNumber === defaultCurrentPage || dataList?.length !== 1) {
      loadPageData();
    } else {
      setCurrentPageNumber((prevPageNumber) => prevPageNumber - 1);
    }
  }
};

export const copyTextHandler = (url, msg) => {
  const configMessage = {
    content: msg,
    duration: 3,
  };
  navigator?.clipboard?.writeText(url);
  message.info(configMessage);
};

export const mapErrorToForm = (errorObj, formSetErrorMethod, scrollToField) => {
  if (typeof errorObj === "object") {
    const fieldError = Object.keys(errorObj).map((item) => ({ name: item, errors: errorObj[item] }));
    formSetErrorMethod?.(fieldError);
    if (fieldError && fieldError.length) {
      scrollToField?.(fieldError?.[0]);
    }
  }
};

export const getTenantUrl = () => {
  let tenantUrl = localStorageHelper.get("tenantUrl");
  if (tenantUrl) {
    return tenantUrl?.includes("/") ? `${getEnv()}${tenantUrl}` : `${getEnv()}/${tenantUrl}`;
  } else {
    return getEnv();
  }
};

export const getWorkspaceUrl = () => {
  let tenantUrl = JSON.parse(localStorageHelper.get("wsmTenantUrl"));
  if (tenantUrl) {
    return tenantUrl;
  } else {
    return getEnv();
  }
};

export const checkIfB2CUser = () => {
  const isB2C = localStorageHelper.get("is_mark_for_b2c") === "undefined" ? false : JSON.parse(localStorageHelper.get("is_mark_for_b2c"));
  const role = getUserRole();
  return isB2C && role === roleName.tenantUser ? true : false;
};

export const getUserID = () => {
  let userID = localStorageHelper.get("userId");
  if (userID) {
    return userID;
  } else return null;
};

export const getErrorMsg = (msg) => {
  switch (msg) {
    case "InvalidTenancyName":
      return "Invalid tenant name.";
    case "InvalidUserNameOrEmailAddress":
      return "You have entered Invalid Username/Email";
    case "TenantIsNotActive":
      return "Tenant is not active.";
    default:
      return "Invalid Password";
  }
};

export const getUtcDateWithFormat = (date, format) => {
  return `${moment(date).local().format(dateTimeFormat3)}`;
};

export const getHoursFromMinutes = (mins, t) => {
  let hrs = mins / 60,
    minutes = mins % 60;
  switch (mins) {
    case mins <= 0:
      return t("common.00");
    case mins < 24 * 60:
      return moment.utc().hours(hrs).minutes(minutes).format(sixthTimeFormat);
    default:
      if (minutes < 10) minutes = t("common.zero").concat(minutes);
      return Math.floor(hrs) + ":" + minutes;
  }
};

export const filterTenantList = (input, option) => {
  return option?.children?.[1]?.toLowerCase()?.indexOf(input?.toLowerCase()) >= 0;
};

// function to set tenant specific custom theme
export const setCustomTenantTheme = (customTheme, root) => {
  if (customTheme && customTheme.hasOwnProperty("custom_primaryColor") && customTheme.hasOwnProperty("custom_secondaryColor")) {
    root.style.setProperty("--primaryColor", customTheme?.custom_primaryColor);
    root.style.setProperty("--secondaryColor", customTheme?.custom_secondaryColor);
    if (customTheme?.custom_primaryColor !== null) {
      const primaryLighter = LightenDarkenColor(customTheme?.custom_primaryColor, 235);
      root.style.setProperty("--primaryLighter", primaryLighter.includes("#") ? primaryLighter : "#".concat(primaryLighter));
    }
  }
};

export const setCustomTheme = (data) => {
  var root = document.querySelector(":root");
  setCustomThemeInLocalStorage(data);
  const customTheme = data && data?.custom_theme ? data?.custom_theme : {};
  setCustomTenantTheme(customTheme, root);
};

export const getPrimaryColor = () => {
  if ("custom_theme" in localStorage) {
    const primaryColor = JSON.parse(localStorage.getItem("custom_theme"))?.custom_primaryColor;
    return primaryColor;
  }
};

export const downloadJSONFile = (obj) => {
  const myData = obj; // I am assuming that "this.state.myData"
  // is an object and I wrote it to file as
  // json

  // create file in browser
  const fileName = "lab_instructions";
  const json = JSON.stringify(myData, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const href = URL.createObjectURL(blob);

  // create "a" HTLM element with href to file
  const link = document.createElement("a");
  link.href = href;
  link.download = fileName + ".json";
  document.body.appendChild(link);
  link.click();

  // clean up "a" element & remove ObjectURL
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
};

export const createCSVLink = (data, fileName) => {
  let csvFile = new Blob([data], { type: "text/csv" });
  let downloadLink = document.createElement("a");
  downloadLink.download = fileName;
  downloadLink.href = window.URL.createObjectURL(csvFile);
  downloadLink.style.display = "none";
  return document.body.appendChild(downloadLink);
};

export const delay = (ms) => new Promise((res) => setTimeout(res, ms));

export const convertNumberToWords = (amount) => {
  let words = new Array();
  words[0] = "";
  words[1] = "One";
  words[2] = "Two";
  words[3] = "Three";
  words[4] = "Four";
  words[5] = "Five";
  words[6] = "Six";
  words[7] = "Seven";
  words[8] = "Eight";
  words[9] = "Nine";
  words[10] = "Ten";
  words[11] = "Eleven";
  words[12] = "Twelve";
  words[13] = "Thirteen";
  words[14] = "Fourteen";
  words[15] = "Fifteen";
  words[16] = "Sixteen";
  words[17] = "Seventeen";
  words[18] = "Eighteen";
  words[19] = "Nineteen";
  words[20] = "Twenty";
  words[30] = "Thirty";
  words[40] = "Forty";
  words[50] = "Fifty";
  words[60] = "Sixty";
  words[70] = "Seventy";
  words[80] = "Eighty";
  words[90] = "Ninety";
  amount = amount.toString();
  let atemp = amount.split(".");
  let number = atemp[0].split(",").join("");
  let n_length = number.length;
  let words_string = "";
  if (n_length <= 9) {
    let n_array = new Array(0, 0, 0, 0, 0, 0, 0, 0, 0);
    let received_n_array = new Array();
    for (let i = 0; i < n_length; i++) {
      received_n_array[i] = number.substr(i, 1);
    }
    for (let i = 9 - n_length, j = 0; i < 9; i++, j++) {
      n_array[i] = received_n_array[j];
    }
    for (let i = 0, j = 1; i < 9; i++, j++) {
      if (i == 0 || i == 2 || i == 4 || i == 7) {
        if (n_array[i] == 1) {
          n_array[j] = 10 + parseInt(n_array[j]);
          n_array[i] = 0;
        }
      }
    }
    let value = "";
    for (let i = 0; i < 9; i++) {
      if (i == 0 || i == 2 || i == 4 || i == 7) {
        value = n_array[i] * 10;
      } else {
        value = n_array[i];
      }
      if (value != 0) {
        words_string += words[value] + " ";
      }
      if ((i == 1 && value != 0) || (i == 0 && value != 0 && n_array[i + 1] == 0)) {
        words_string += "Crores ";
      }
      if ((i == 3 && value != 0) || (i == 2 && value != 0 && n_array[i + 1] == 0)) {
        words_string += "Lakhs ";
      }
      if ((i == 5 && value != 0) || (i == 4 && value != 0 && n_array[i + 1] == 0)) {
        words_string += "Thousand ";
      }
      if (i == 6 && value != 0 && n_array[i + 1] != 0 && n_array[i + 2] != 0) {
        words_string += "Hundred and ";
      } else if (i == 6 && value != 0) {
        words_string += "Hundred ";
      }
    }
    words_string = words_string.split("  ").join(" ");
  }
  return words_string;
};
export const equalsCheck = (a, b) => {
  return JSON.stringify(a) === JSON.stringify(b);
};
